import { useState, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BarChart3 } from 'lucide-react';

interface ChartsProps {
  finance?: any[];
  companies?: any[];
}

export default function Charts({ finance = [], companies = [] }: ChartsProps) {
  const [activeTab, setActiveTab] = useState<'revenue' | 'companies'>('revenue');

  const processedData = useMemo(() => {
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Last 6 months
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(currentYear, currentMonth - i, 1);
      last6Months.push({
        month: d.getMonth(),
        year: d.getFullYear(),
        name: monthNames[d.getMonth()],
        revenue: 0,
        companies: 0,
      });
    }

    // Accumulate Revenue
    finance.forEach((entry) => {
      if (entry.type !== 'RECEITA') return;
      const d = new Date(entry.date || entry.createdAt);
      const m = d.getMonth();
      const y = d.getFullYear();
      
      const bucket = last6Months.find(b => b.month === m && b.year === y);
      if (bucket) {
        bucket.revenue += Number(entry.amount) || 0;
      }
    });

    // Accumulate Companies (new companies per month)
    companies.forEach((company) => {
      const d = new Date(company.createdAt);
      const m = d.getMonth();
      const y = d.getFullYear();
      
      const bucket = last6Months.find(b => b.month === m && b.year === y);
      if (bucket) {
        bucket.companies += 1;
      }
    });

    return last6Months;
  }, [finance, companies]);

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `R$ ${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `R$ ${(value / 1000).toFixed(0)}k`;
    return `R$ ${value}`;
  };

  return (
    <div className="bg-[#F8FAFC]/40 backdrop-blur-md border border-white rounded-[40px] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.05)] relative overflow-hidden group">
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/10 rounded-full blur-3xl pointer-events-none group-hover:bg-indigo-50/20 transition-colors duration-700" />

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 relative z-10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white border border-slate-100 text-slate-900 flex items-center justify-center shadow-sm group-hover:border-indigo-100 transition-colors">
            <BarChart3 size={18} className="text-[#0F172A]" />
          </div>
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 leading-none">Análise Histórica</span>
            <h3 className="text-sm font-display font-black text-[#0F172A] tracking-tight leading-none">
              {activeTab === 'revenue' ? 'Crescimento da Receita' : 'Novas Empresas'}
            </h3>
          </div>
        </div>
        
        <div className="flex items-center gap-1 bg-slate-100/80 p-0.5 rounded-lg border border-slate-200/80">
          <button 
            onClick={() => setActiveTab('revenue')}
            className={`text-[10px] font-bold px-3 py-1 rounded-md transition-all cursor-pointer uppercase tracking-wider ${activeTab === 'revenue' ? 'text-slate-900 bg-white shadow-2xs' : 'text-slate-500 hover:text-slate-900'}`}
          >
            Receita
          </button>
          <button 
            onClick={() => setActiveTab('companies')}
            className={`text-[10px] font-bold px-3 py-1 rounded-md transition-all cursor-pointer uppercase tracking-wider ${activeTab === 'companies' ? 'text-slate-900 bg-white shadow-2xs' : 'text-slate-500 hover:text-slate-900'}`}
          >
            Empresas
          </button>
        </div>
      </div>

      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={processedData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0F172A" stopOpacity={0.14}/>
                <stop offset="95%" stopColor="#0F172A" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748B', fontSize: 10, fontWeight: 600 }}
              dy={10}
            />
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fill: '#64748B', fontSize: 10, fontWeight: 600 }}
              tickFormatter={activeTab === 'revenue' ? formatYAxis : (val) => val}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#0F172A', 
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                padding: '10px 14px',
                boxShadow: '0 10px 25px -5px rgba(15,23,42,0.25)'
              }}
              itemStyle={{ color: '#fff' }}
              cursor={{ stroke: '#0F172A', strokeWidth: 1, strokeDasharray: '3 3' }}
              formatter={(value: number) => [
                activeTab === 'revenue' ? formatCurrency(value) : `${value} empresas`, 
                activeTab === 'revenue' ? 'Receita' : 'Empresas'
              ]}
            />
            <Area 
              type="monotone" 
              dataKey={activeTab === 'revenue' ? 'revenue' : 'companies'} 
              stroke="#0F172A" 
              strokeWidth={2.2}
              fillOpacity={1} 
              fill="url(#colorValue)" 
              activeDot={{ r: 5, fill: '#0F172A', stroke: '#fff', strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
